import React from 'react';
import styled from 'styled-components';
import { config } from '@keytrade/functions';
import { Span } from '@keytrade/components-span';
import { CardImage } from '@keytrade/components-cardImage';
import { Icon } from '@keytrade/components-icon';

import Title from '@/components/Title';
import BlockWrapper from '@/components/BlockWrapper';
import Link, { LinkStyle } from '@/components/Link';
import Container from '@/components/Container';
import Paragraph from '@/components/Paragraph';
import CurrencyFormatter from '@/formatters/CurrencyFormatter';
import { VisaComparisonTableBlockType } from '@/components/blocks/VisaComparisonTableBlock';
import { mediaQuery } from '@/utils/helpers';
import { capitalize } from '@/utils/StringUtils';
import useTranslations from '@/hooks/useTranslations';

const { list: colors } = config.colors;
const { getFont } = config.fonts;
const InfoBlock = styled(Container)`
  text-align: center;
`;

const TableContent = styled.div`
  border: 1px solid ${colors.BlueLight};
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05),
    0px 2px 7px 0px rgba(0, 0, 0, 0.05);
  background: white;
  padding: 3.2rem 0;
  border-radius: 2.4rem;
  position: relative;
  top: -12.4rem;
`;

const Table = styled.table`
  table-layout: fixed;
  width: 100%;
  border-collapse: collapse;
`;

const SectionRow = styled.tr`
  border-bottom: 1px solid ${colors.BlueFog};
  > td {
    padding: 4.8rem 0 2rem;
    &:first-child {
      text-align: left;
      padding-left: 3.2rem;
    }
  }
`;
const ValueRow = styled.tr`
  border-bottom: 1px solid ${colors.BlueLight};
  > td {
    padding: 2rem;
    &:first-child {
      text-align: left;
      padding-left: 3.2rem;
    }
  }
`;
const Column = styled.td`
  text-align: center;
`;

const ParameterTitle = styled.p`
  color: ${colors.BlueDark};
  font-family: ${({ theme }) => getFont(theme, 'bold')};
  font-size: 16px;
  line-height: 24px;
`;
const ParameterDescription = styled.p`
  color: ${colors.BlueMoon};
  font-family: ${({ theme }) => getFont(theme, 'regular')};
  font-size: 14px;
  line-height: 20px;
`;
const CTAContent = styled.div`
  padding: 6.4rem 0 3.2rem;
  max-width: 17rem;
  margin: 0 auto;
`;
type SmallProps = {
  margin?: string;
  color?: string;
};
const Small = styled.p<SmallProps>`
  font-size: 14px;
  line-height: 20px;
  ${({ margin }) => (margin ? `margin: ${margin};` : '')}
  ${({ color }) => (color ? `color: ${color};` : '')}
`;
const Disclaimers = styled.div`
  padding: 3.2rem 3.2rem 0;
  position: relative;
  top: -12.4rem;
`;
const BlockWrapperWithGreyTop = styled(BlockWrapper)`
  position: relative;
  z-index: 2;
`;
type Props = VisaComparisonTableBlockType;
const DesktopBlock = styled.div`
  display: none;
  background-color: ${colors.GreyBackground};
  ${mediaQuery.md('display:block;')};
`;
const VisaComparisonTableBlockDesktop: React.FC<Props> = ({
  id,
  title,
  subtitleMultipleLine: { subtitle },
  classicPrimaryLink,
  classicSecondaryLink,
  goldPrimaryLink,
  goldSecondaryLink,
  platinumPrimaryLink,
  platinumSecondaryLink,
}) => {
  const { t } = useTranslations('visa_comparison_table');

  const sectionRow = (title) => {
    return (
      <SectionRow>
        <Column>
          <Title size='sm' level='h3'>
            {title}
          </Title>
        </Column>
        <Column>
          <Paragraph color='GreyBlue' size='sm' weight='bold'>
            {t('classic')}
          </Paragraph>
        </Column>
        <Column>
          <Paragraph color='GreyBlue' size='sm' weight='bold'>
            {t('gold')}
          </Paragraph>
        </Column>
        <Column>
          <Paragraph color='GreyBlue' size='sm' weight='bold'>
            {t('platinum')}
          </Paragraph>
        </Column>
      </SectionRow>
    );
  };
  const valueColumn = (value, disclaimer, color, bold) => {
    return (
      <Column>
        {value === 'x' && (
          <Icon icon='icn_crossLight' color={colors.BlueCloudy} />
        )}
        {value === 'v' && <Icon icon='icn_check' color={colors.BlueSky} />}
        {value !== 'x' && value !== 'v' && typeof value === 'string' && (
          <Paragraph color='BlueDark' size='sm'>
            <CurrencyFormatter>
              <Span
                color={color ?? 'BlueDark'}
                weight={bold ? 'bold' : 'regular'}
              >
                {value}
              </Span>
              {disclaimer === 1 && <sup>*</sup>}
              {disclaimer === 2 && <sup>**</sup>}
            </CurrencyFormatter>
          </Paragraph>
        )}
        {value !== 'x' && value !== 'v' && typeof value !== 'string' && value}
      </Column>
    );
  };
  const valueRow = (
    title,
    description,
    value1,
    value2,
    value3,
    disclaimer = null,
    color = null,
    bold = false,
    disclaimerTitle = null,
  ) => {
    return (
      <ValueRow>
        <Column>
          <ParameterTitle>
            {title}
            {disclaimerTitle === 1 && <sup>*</sup>}
            {disclaimerTitle === 2 && <sup>**</sup>}
          </ParameterTitle>
          {description && (
            <ParameterDescription>{description}</ParameterDescription>
          )}
        </Column>
        {valueColumn(value1, disclaimer, color, bold)}
        {valueColumn(value2, disclaimer, color, bold)}
        {valueColumn(value3, disclaimer, color, bold)}
      </ValueRow>
    );
  };

  const endRow = (type, primaryLink, secondaryLink) => {
    return (
      <CTAContent>
        <CardImage type={`cardVisa${capitalize(type)}`} imgWidth='180px' />
        <Title size='sm' level='h2' margin='2rem 0'>
          {t(`visa_${type}`)}
        </Title>
        <Link
          {...primaryLink}
          linkStyle={LinkStyle.PRIMARY}
          fullWidth={true}
          margin='0 0 2rem'
        />
        <Link {...secondaryLink} linkStyle={LinkStyle.SECONDARY} />
      </CTAContent>
    );
  };
  return (
    <DesktopBlock>
      <BlockWrapper id={id} color='Grey Background' padding='bottomOnly'>
        <InfoBlock narrow>
          <Title size='xxxl' level='h1' margin='1.2rem 0 1.6rem'>
            {title}
          </Title>
          {subtitle && (
            <Paragraph color='GreyBlue' weight='bold' margin='0 0 7.2rem'>
              {subtitle}
            </Paragraph>
          )}
        </InfoBlock>
      </BlockWrapper>
      <BlockWrapperWithGreyTop color='White' padding='none'>
        <Container>
          <TableContent>
            <Table>
              <tbody>
                <tr>
                  <Column></Column>
                  <Column>
                    <Title size='md' level='h2' margin='0 0 2rem'>
                      {t('visa_classic')}
                    </Title>
                    <CardImage type={'cardVisaClassic'} imgWidth='180px' />
                  </Column>
                  <Column>
                    <Title size='md' level='h2' margin='0 0 2rem'>
                      {t('visa_gold')}
                    </Title>
                    <CardImage type={'cardVisaGold'} imgWidth='180px' />
                  </Column>
                  <Column>
                    <Title size='md' level='h2' margin='0 0 2rem'>
                      {t('visa_platinum')}
                    </Title>
                    <CardImage type={'cardVisaPlatinum'} imgWidth='180px' />
                  </Column>
                </tr>
                {sectionRow(t('main_features'))}
                {valueRow(
                  t('yearly_cost'),
                  null,
                  t('free'),
                  t('free'),
                  t('free'),
                  1,
                  'GreenSeafoam',
                  true,
                )}
                {valueRow(
                  t('max_monthly_limit'),
                  null,
                  '2000EUR',
                  '5000EUR',
                  '10000EUR',
                  null,
                  null,
                  false,
                  2,
                )}
                {valueRow(
                  t('repayment'),
                  null,
                  t('total_debited_beginning_of_month'),
                  t('total_debited_beginning_of_month'),
                  t('total_debited_beginning_of_month'),
                )}
                ,
                {valueRow(
                  t('settings_via_app_and_website'),
                  null,
                  'v',
                  'v',
                  'v',
                )}
                {valueRow(t('contactless'), null, 'v', 'v', 'v')}
                {valueRow(
                  t('withdraw_in_the_eurozone'),
                  null,
                  t('free'),
                  t('free'),
                  t('free'),
                  null,
                  'GreenSeafoam',
                  true,
                )}
                {sectionRow(t('purchases'))}
                {valueRow(
                  t('purchase_insurance'),
                  t('per_claim_per_year'),
                  '1500EUR',
                  '2000EUR',
                  '5000EUR',
                )}
                {valueRow(
                  t('safe_online'),
                  t('per_claim_per_year'),
                  '750EUR',
                  '1000EUR',
                  '1500EUR',
                )}
                {valueRow(t('warranty_extension'), null, 'x', 'v', 'v')}
                {sectionRow(t('travel_insurances'))}
                {valueRow(
                  t('travel_accident'),
                  null,
                  'x',
                  '100000EUR',
                  '200000EUR',
                )}
                {valueRow(
                  t('travel_assistance'),
                  null,
                  'x',
                  t('light_service'),
                  t('full_service'),
                )}
                {valueRow(
                  t('travel_cancellation'),
                  t('or_interruption'),
                  'x',
                  '5000EUR',
                  '5000EUR',
                )}
                {valueRow(
                  t('travel_disruption'),
                  t('delayed_or_cancelled_departure'),
                  'x',
                  '250EUR',
                  '250EUR',
                )}
                {sectionRow(t('theft_and_loss_insurances'))}
                {valueRow(
                  t('cash_theft_after_a_withdrawal'),
                  null,
                  'x',
                  '1000EUR',
                  '1000EUR',
                )}
                {valueRow(
                  t('keys_and_papers'),
                  t('per_claim_per_year'),
                  'x',
                  'x',
                  '500EUR',
                )}
                {sectionRow(t('other_services'))}
                {valueRow(t('caretaking_services'), null, 'x', 'x', 'v')}
                {valueRow(
                  t('ticket_cancellation'),
                  null,
                  'x',
                  'x',
                  <>
                    <Paragraph color='GreyBlue'>
                      <CurrencyFormatter>
                        {`250EUR ${t('per_ticket')}`}
                      </CurrencyFormatter>
                    </Paragraph>
                    <Small color={colors.GreyBlue}>
                      <CurrencyFormatter>
                        {`1000EUR ${t('per_year')}`}
                      </CurrencyFormatter>
                    </Small>
                  </>,
                )}
                <tr>
                  <Column></Column>
                  <Column>
                    {endRow(
                      'classic',
                      classicPrimaryLink,
                      classicSecondaryLink,
                    )}
                  </Column>
                  <Column>
                    {endRow('gold', goldPrimaryLink, goldSecondaryLink)}
                  </Column>
                  <Column>
                    {endRow(
                      'platinum',
                      platinumPrimaryLink,
                      platinumSecondaryLink,
                    )}
                  </Column>
                </tr>
              </tbody>
            </Table>
          </TableContent>
          <Disclaimers>
            <Small color={colors.GreyBlue} margin='0 0 1.6rem'>
              {t('disclaimer1')}
            </Small>
            <Small color={colors.GreyBlue}>{t('disclaimer2')}</Small>
          </Disclaimers>
        </Container>
      </BlockWrapperWithGreyTop>
    </DesktopBlock>
  );
};

export default VisaComparisonTableBlockDesktop;
